import { Link } from "react-router-dom";
import { House, Buildings, CheckCircle, ArrowRight } from "@phosphor-icons/react";

/**
 * Malaysia-only premium section shown between hero and services on the Malaysia
 * country page. Two option cards (MM2H + Labuan) side-by-side on desktop,
 * stacked on mobile. Matches the current premium/Canadian theme (bone bg,
 * navy accents, sand highlights, serif display headings).
 */
export default function MalaysiaPrograms() {
  return (
    <section
      className="py-20 bg-gos-bone border-b border-gos-border"
      data-testid="malaysia-programs"
    >
      <div className="container-gos">
        <div className="max-w-3xl mb-12">
          <div className="overline mb-3">Malaysia programmes</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">
            Two clear pathways into Malaysia
          </h2>
          <p className="text-gos-slate mt-4 leading-relaxed">
            Whether you&apos;re relocating your family or expanding your business,
            we offer end-to-end professional support tailored to your goals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
          <ProgramCard
            icon={House}
            badge="Lifestyle & Residency"
            title="Malaysia My Second Home (MM2H)"
            price="USD 8,000–12,000*"
            priceLabel="Indicative cost"
            description="Ideal for individuals and families seeking long-term residence in Malaysia."
            bullets={[
              "Long-term residency pathway",
              "Suitable for retirees, investors & families",
              "Eligibility assessment",
              "Document preparation",
              "End-to-end application support",
            ]}
            footnote="*Professional service fees only. Government fees, deposits and official requirements are additional and subject to the applicable MM2H programme."
            testid="malaysia-card-mm2h"
          />

          <ProgramCard
            icon={Buildings}
            badge="Business Expansion"
            title="Labuan Business Setup & Work Permit"
            price="Starting from USD 15,000"
            priceLabel="Indicative cost"
            description="Establish a Labuan company with business support, work permit assistance and family relocation services."
            bullets={[
              "100% Foreign Ownership",
              "Up to 4 Work Permits",
              "Family Dependent Visas",
              "Business-Friendly Tax Structure",
              "Banking Assistance",
              "Registered Office & Company Secretary",
              "Ongoing Compliance Support",
            ]}
            footnote="Pricing is indicative and depends on business activities, licensing and government requirements."
            testid="malaysia-card-labuan"
          />
        </div>
      </div>
    </section>
  );
}

function ProgramCard({ icon: Icon, badge, title, price, priceLabel, description, bullets, footnote, testid }) {
  return (
    <div
      className="bg-white border border-gos-border rounded-sm p-8 md:p-10 flex flex-col h-full card-lift"
      data-testid={testid}
    >
      {/* Badge + icon */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <span className="overline text-gos-sand bg-gos-bone border border-gos-border px-3 py-1.5 rounded-sm">
          {badge}
        </span>
        <div className="w-11 h-11 flex items-center justify-center bg-gos-navy text-gos-cream rounded-sm">
          <Icon size={20} weight="duotone" />
        </div>
      </div>

      {/* Title */}
      <h3 className="font-display text-2xl text-gos-navy leading-tight">{title}</h3>

      {/* Description */}
      <p className="text-sm text-gos-slate mt-3 leading-relaxed">{description}</p>

      {/* Price */}
      <div className="mt-6 border-t border-gos-border pt-6">
        <div className="overline !text-gos-muted mb-1">{priceLabel}</div>
        <div className="font-display text-2xl text-gos-charcoal">{price}</div>
      </div>

      {/* Bullets */}
      <ul className="mt-6 space-y-2.5 flex-grow">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-sm text-gos-charcoal">
            <CheckCircle size={16} weight="fill" className="text-gos-maple mt-0.5 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {/* Footnote */}
      <p className="text-[11px] text-gos-muted italic mt-6 leading-relaxed">{footnote}</p>

      {/* CTA */}
      <div className="mt-6">
        <Link
          to="/contact?country=malaysia"
          className="btn-primary w-full justify-center"
          data-testid={`${testid}-cta`}
        >
          Book Consultation <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}
